import React, { useEffect, useState } from 'react';
import { Radar } from 'lucide-react';
import PillarRadarChart from './PillarRadarChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Self-fetching six-pillar radar card for the organisation dashboard. */
export const PillarRadarCard = ({ organizationId, onStartAssessment }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compare, setCompare] = useState('all');

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    let active = true;
    fetch(`${BACKEND_URL}/api/organization/${organizationId}/pillar-radar?compare=${compare}`)
      .then((r) => r.json())
      .then((d) => active && setData(d))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [organizationId, compare]);

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200" data-testid="pillar-radar-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-indigo-100 rounded-lg p-2">
          <Radar className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Six-Pillar Performance</h2>
          <p className="text-sm text-gray-500">
            {data?.has_scores ? 'Your latest evaluated scores across the six pillars' : 'Available once your assessment is evaluated'}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="radar-compare-toggle">
          {[
            { key: 'all', label: 'All certified peers' },
            { key: 'size', label: 'My size' },
          ].map((option) => (
            <button
              key={option.key}
              onClick={() => setCompare(option.key)}
              data-testid={`radar-compare-${option.key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                compare === option.key ? 'bg-white text-indigo-700 shadow' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading your pillar scores…</div>
      ) : (
        <>
          <PillarRadarChart
            pillars={data?.pillars}
            weakest={data?.weakest_pillar}
            hint={data?.improvement_hint}
            overallScore={data?.overall_score}
            peer={data?.peer_comparison}
          />
          {!data?.has_scores && (
            <div className="mt-6 rounded-xl bg-indigo-50 border border-indigo-200 p-4 flex items-center justify-between gap-4">
              <p className="text-sm text-indigo-900">
                Complete your assessment to see how you score on each of the six pillars.
              </p>
              {onStartAssessment && (
                <button
                  onClick={onStartAssessment}
                  data-testid="radar-start-assessment-btn"
                  className="shrink-0 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Go to Assessment
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PillarRadarCard;
